import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { Store } from './types';
import Logger from '../../utils/Logger';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Push Notification Panel Component
 *
 * Dedicated control panel for sending push notifications
 * - Send to store channel
 * - Send globally to all users
 * - Send to individual users
 * - Send to multiple selected users
 */
const PushNotificationPanel: React.FC = () => {
  const [recipientType, setRecipientType] = useState<'global' | 'store' | 'individual' | 'multiple'>('global');
  const [targetStoreId, setTargetStoreId] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const notify = useNotify();

  /**
   * Fetch stores for dropdown
   */
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data } = await authInstance.get('/admin/stores');
        setStores(data || []);
      } catch (err: any) {
        Logger.error('Error fetching stores:', err);
      }
    };

    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetch users for selection (only when needed)
   */
  const fetchUsers = async () => {
    try {
      const { data } = await authInstance.get('/admin/users?limit=100');
      setUsers(data.users || []);
    } catch (err: any) {
      Logger.error('Error fetching users:', err);
      notify('Failed to load users', { type: 'error' });
    }
  };

  useEffect(() => {
    if (recipientType === 'individual' || recipientType === 'multiple') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientType]);

  const handleSend = () => {
    // Validate
    if (!title.trim()) {
      notify('Title is required', { type: 'warning' });
      return;
    }

    if (!message.trim()) {
      notify('Message is required', { type: 'warning' });
      return;
    }

    if (recipientType === 'store' && !targetStoreId) {
      notify('Please select a store', { type: 'warning' });
      return;
    }

    if (recipientType === 'individual' && !selectedUser) {
      notify('Please select a user', { type: 'warning' });
      return;
    }

    if (recipientType === 'multiple' && selectedUsers.length === 0) {
      notify('Please select at least one user', { type: 'warning' });
      return;
    }

    // Show confirmation for global
    if (recipientType === 'global') {
      setConfirmDialogOpen(true);
      return;
    }

    sendPushNotification();
  };

  const sendPushNotification = async () => {
    setLoading(true);

    try {
      let recipientIds: string[] = [];

      if (recipientType === 'individual' && selectedUser) {
        recipientIds = [selectedUser.id];
      } else if (recipientType === 'multiple') {
        recipientIds = selectedUsers.map(u => u.id);
      }

      const payload = {
        recipientType,
        targetStoreId: recipientType === 'store' ? targetStoreId : null,
        recipientIds: recipientIds.length > 0 ? recipientIds : null,
        title,
        message,
      };

      await authInstance.post('/admin/push-notifications-panel/send', payload);

      notify('Push notification sent successfully!', { type: 'success' });

      // Reset form
      setTitle('');
      setMessage('');
      setSelectedUser(null);
      setSelectedUsers([]);
      if (recipientType === 'store') {
        setTargetStoreId('');
      }

      setConfirmDialogOpen(false);
    } catch (err: any) {
      const errorMessage = err?.body?.message || err?.message || 'Failed to send push notification';
      notify(errorMessage, { type: 'error' });
      Logger.error('Error sending push notification:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📱 Push Notification Control Panel
          </Typography>

          <FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Recipient Type
            </Typography>
            <RadioGroup
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value as any)}
            >
              <FormControlLabel
                value="global"
                control={<Radio />}
                label="🌍 Global (All Users)"
              />
              <FormControlLabel
                value="store"
                control={<Radio />}
                label="🏪 Store Channel"
              />
              <FormControlLabel
                value="individual"
                control={<Radio />}
                label="👤 Individual User"
              />
              <FormControlLabel
                value="multiple"
                control={<Radio />}
                label="👥 Multiple Users"
              />
            </RadioGroup>
          </FormControl>

          {recipientType === 'store' && (
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Select Store</InputLabel>
              <Select
                value={targetStoreId}
                onChange={(e) => setTargetStoreId(e.target.value)}
                label="Select Store"
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {recipientType === 'individual' && (
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.email} ${user.firstName ? `(${user.firstName} ${user.lastName})` : ''}`}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select User" />
                )}
              />
            </FormControl>
          )}

          {recipientType === 'multiple' && (
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(user) => `${user.email} ${user.firstName ? `(${user.firstName} ${user.lastName})` : ''}`}
                value={selectedUsers}
                onChange={(_, newValue) => setSelectedUsers(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Users" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.email}
                        {...tagProps}
                      />
                    );
                  })
                }
              />
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Notification Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 100 }}
            helperText={`${title.length}/100 characters`}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notification Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            inputProps={{ maxLength: 200 }}
            helperText={`${message.length}/200 characters`}
            sx={{ mt: 2 }}
          />

          {recipientType === 'global' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              ⚠️ This will send a push notification to ALL users
            </Alert>
          )}

          {recipientType === 'multiple' && selectedUsers.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Will send to {selectedUsers.length} selected user{selectedUsers.length > 1 ? 's' : ''}
            </Alert>
          )}

          <Button
            variant="contained"
            color="secondary"
            onClick={handleSend}
            sx={{ mt: 3 }}
            disabled={loading || !title || !message}
            fullWidth
          >
            📱 Send Push Notification
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog for Global Push */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>⚠️ Confirm Global Push Notification</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>Warning:</strong> You are about to send a push notification to <strong>ALL users</strong>.
            <br /><br />
            This will appear as a native push notification on their devices.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={sendPushNotification}
            variant="contained"
            color="secondary"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Confirm Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PushNotificationPanel;
